"use client";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import Tooltip from "@mui/material/Tooltip";
import transform from "css-to-react-native-transform";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Container, Draggable } from "react-smooth-dnd";
import { v4 as uuid } from "uuid";

import ComponentModal from "./components/component-modal";
import DEFAULT_ITEMS from "./components/component-properties/default-props";
import {
  GRID,
  DYNAMICTABLE,
  DYNAMICLIST,
  STEPPER,
} from "./components/constants";
import {
  createGrid,
  updateGrid,
  removeItemFromGrid,
  editItemOnGrid,
  findItemOnGrid,
} from "./components/helper";
import Menu from "./components/menu";
import PropertiesModal from "./components/properties-modal";
import StyledField from "./components/styledField";
import { ModelerDecisionModal } from "./modeler-decision-modal";
import MaterialModal from "../../components/MaterialModal";
import { language } from "../../language";

function EllipsisStepButton({ label, onClick }) {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(
          textRef.current.scrollWidth > textRef.current.clientWidth,
        );
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [label]);

  const button = (
    <StepButton
      onClick={onClick}
      sx={{
        "& .MuiStepLabel-label": {
          maxWidth: 70,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
    >
      <span
        ref={textRef}
        style={{
          display: "inline-block",
          maxWidth: 70,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </StepButton>
  );

  if (isOverflowing) {
    return (
      <Tooltip
        title={label}
        arrow
        placement="top"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -25],
                },
              },
            ],
          },
        }}
      >
        {button}
      </Tooltip>
    );
  }

  return button;
}

const FormBuilder = (props) => {
  const {
    onChange,
    form,
    metadatas,
    canCreateMetadata,
    allFormFields,
    businessObject,
  } = props;

  const [lines, setLines] = useState(
    form && form.components ? form.components : [],
  );
  const [fields] = useState(DEFAULT_ITEMS);
  const [open, setOpen] = useState(false);
  const [openModalDecision, setOpenModalDecision] = useState(false);
  const [cellData, setCellData] = useState({});
  const [edit, setEdit] = useState(false);
  const [obj, setObj] = useState(false);
  const [objIndex, setObjIndex] = useState(null);
  const [gridParams, setGridParams] = useState(false);
  const [openComponent, setOpenComponent] = useState(false);
  const [originColumn, setOriginColumn] = useState(0);
  const [originLine, setOriginLine] = useState(0);
  const [manage, setManage] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [stepperActive, setStepperActive] = useState({});
  const [pendingStepperDrop, setPendingStepperDrop] = useState(null);
  const [editingStepperInfo, setEditingStepperInfo] = useState(null);

  const openingComponentModalRef = useRef(false);

  useEffect(() => {
    if (!openComponent) {
      openingComponentModalRef.current = false;
    }
  }, [openComponent]);

  const closeComponentModal = useCallback(() => {
    setPendingStepperDrop(null);
    openingComponentModalRef.current = false;
    setOpenComponent(false);
  }, []);

  const openComponentModal = useCallback((dropPayload) => {
    if (openingComponentModalRef.current) {
      return;
    }
    openingComponentModalRef.current = true;
    setManage(Object.assign({}, dropPayload));
    setOpenComponent(true);
  }, []);

  useEffect(() => {
    if (errorModalOpen) {
      closeComponentModal();
      setManage(false);
    }
  }, [errorModalOpen, closeComponentModal]);

  useEffect(() => {
    if (onChange) {
      onChange({ lines, fields });
    }
  }, [lines, onChange, fields]);

  useEffect(() => {
    const stepperIdx = lines.findIndex((it) => it && it.type === STEPPER);
    if (stepperIdx > 0) {
      const newArr = [...lines];
      const [stepperItem] = newArr.splice(stepperIdx, 1);
      newArr.unshift(stepperItem);
      setLines(newArr);
    }
  }, [lines]);

  const handleEdit = useCallback(
    (event, item) => {
      const itemId = item.id;
      let foundIndex = null;
      let foundItem = lines.find((element) => element.id === itemId);

      if (foundItem) {
        foundIndex = lines.findIndex((element) => element.id === itemId);
      } else {
        foundItem = findItemOnGrid(item, lines);
      }

      if (!foundItem) {
        let locatedStepperInfo = null;
        let locatedItem = null;

        for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
          const line = lines[lineIndex];
          if (!line || line.type !== STEPPER || !Array.isArray(line.items)) {
            continue;
          }

          for (let stepIndex = 0; stepIndex < line.items.length; stepIndex++) {
            const stepContainer = line.items[stepIndex];
            if (!stepContainer || !Array.isArray(stepContainer.items)) {
              continue;
            }

            for (
              let slotIndex = 0;
              slotIndex < stepContainer.items.length;
              slotIndex++
            ) {
              const slot = stepContainer.items[slotIndex];
              const child = slot?.items?.[0];
              const element =
                child && child.element ? child.element : child || null;

              if (element && element.id === itemId) {
                locatedStepperInfo = { lineIndex, stepIndex, slotIndex };
                locatedItem = element;
                break;
              }
            }

            if (locatedItem) {
              break;
            }
          }

          if (locatedItem) {
            break;
          }
        }

        if (locatedItem && locatedStepperInfo) {
          setOpen(true);
          setManage(locatedItem);
          setObjIndex(null);
          setEdit(true);
          setEditingStepperInfo(locatedStepperInfo);
          return;
        }
      } else {
        setEditingStepperInfo(null);
      }

      if (foundItem) {
        setOpen(true);
        setManage(foundItem);
        setObjIndex(foundIndex);
        setEdit(true);
      }
    },
    [lines],
  );

  const setEditComponent = useCallback(
    (componente) => {
      if (editingStepperInfo) {
        const { lineIndex, stepIndex, slotIndex } = editingStepperInfo;
        const updatedLines = [...lines];
        const stepperLine = updatedLines[lineIndex];

        if (
          stepperLine &&
          Array.isArray(stepperLine.items) &&
          stepperLine.items[stepIndex]
        ) {
          const stepContainer = stepperLine.items[stepIndex];

          if (!Array.isArray(stepContainer.items)) {
            stepContainer.items = [];
          }

          if (!stepContainer.items[slotIndex]) {
            stepContainer.items[slotIndex] = { items: [] };
          }

          stepContainer.items[slotIndex].items[0] = componente;
        }

        setObj(false);
        setObjIndex(false);
        setEdit(false);
        setManage(false);
        setEditingStepperInfo(null);
        setLines(updatedLines);
        return;
      }

      const index = objIndex;
      let linesindex = [...lines];

      if (index !== null) {
        if (
          componente.type === GRID ||
          componente.type === DYNAMICTABLE ||
          componente.type === DYNAMICLIST
        ) {
          componente = updateGrid(componente);
        }
        linesindex[index] = componente;
      } else {
        linesindex = editItemOnGrid(componente, lines);
      }

      setObj(false);
      setObjIndex(false);
      setEdit(false);
      setManage(false);
      setLines(linesindex);
    },
    [objIndex, lines, editingStepperInfo],
  );

  const handleDelete = useCallback(
    (event, item) => {
      const itemid = item.id;
      let linesindex = [...lines];
      let removed = false;

      const stepperIndex = linesindex.findIndex((el) => el?.type === STEPPER);
      if (stepperIndex !== -1) {
        const stepper = linesindex[stepperIndex];

        if (stepper?.items && Array.isArray(stepper.items)) {
          for (let stepIdx = 0; stepIdx < stepper.items.length; stepIdx++) {
            const stepItems = stepper.items[stepIdx]?.items;
            if (Array.isArray(stepItems)) {
              for (let itemIdx = 0; itemIdx < stepItems.length; itemIdx++) {
                const slot = stepItems[itemIdx];
                const element = slot?.items?.[0];
                if (element?.id === itemid) {
                  stepItems.splice(itemIdx, 1);
                  removed = true;
                  break;
                }
              }
              if (removed) {
                break;
              }
            }
          }
        }
      }

      if (removed) {
        setLines(linesindex);
        return;
      }

      const find_index = linesindex.findIndex((el) => el.id === itemid);
      if (find_index === -1) {
        linesindex = removeItemFromGrid(item, linesindex);
      } else {
        linesindex.splice(find_index, 1);
      }

      setLines(linesindex);
    },
    [lines],
  );

  const handleClose = useCallback(
    (fromSubmit) => {
      if (!edit && fromSubmit !== true) {
        let updatedLines = [...lines];

        updatedLines = removeItemFromGrid(
          manage && manage.payload && manage.payload.element
            ? manage.payload.element
            : manage.payload,
          updatedLines,
        );
        setLines(updatedLines);
        setManage(false);
      }

      if (fromSubmit !== true) {
        setPendingStepperDrop(null);
      }

      setOpen(!open);
      setEdit(false);
      setObj(false);
      setObjIndex(false);
      setGridParams(false);
      setManage(false);
      setEditingStepperInfo(null);
    },
    [edit, lines, manage, open],
  );

  const shouldAcceptDrop = useCallback((sourceContainerOptions, payload) => {
    return !(
      payload &&
      ((payload.element &&
        (payload.element.type === GRID ||
          payload.element.type === DYNAMICTABLE ||
          payload.element.type === DYNAMICLIST)) ||
        payload.type === GRID ||
        payload.type === DYNAMICTABLE ||
        payload.type === DYNAMICLIST)
    );
  }, []);

  const dropToColumn = useCallback(
    (line, linha, coluna, colNum, linNum, p) => {
      const removedIndex = p ? p.removedIndex : null;
      const addedIndex = p ? p.addedIndex : null;
      const updatedLines = [...lines];

      if (addedIndex !== null && p && p.payload) {
        const isStepper = (objValue) =>
          !!objValue &&
          (objValue.type === STEPPER || objValue?.element?.type === STEPPER);

        const element = p.payload.element || p.payload;
        if (isStepper(element)) {
          return;
        }
      }

      if (removedIndex === null && addedIndex === null) {
        return;
      }
      if (removedIndex !== null) {
        linha.items[colNum].items = [];
      }

      if (p) {
        if (
          p.payload &&
          p.payload.element &&
          p.payload.element.isMetadata &&
          !p.payload.element.uuid
        ) {
          p.payload.element.uuid = uuid();
        } else if (p.payload && p.payload.isMetadata && !p.payload.uuid) {
          p.payload.uuid = uuid();
        }
      }

      if (addedIndex !== null) {
        if (linha.items[colNum].items[0]) {
          setOpenModalDecision(true);
          setCellData({
            line,
            linha,
            coluna,
            colNum,
            linNum,
            p,
          });
        } else {
          linha.items[colNum].items[0] = p.payload
            ? p.payload.element
              ? p.payload.element
              : p.payload
            : null;
        }
        if (
          updatedLines.findIndex(
            ({ uuid: lineUuid }) => lineUuid === p.payload.uuid,
          ) === -1
        ) {
          setLines(updatedLines);
        }
      }
    },
    [lines],
  );

  const renderLine = useCallback(
    (line) => {
      if (line.type === STEPPER) {
        const stepsProp = line?.properties?.find?.((p) => p.id === "steps") || {
          value: [],
        };
        const steps = Array.isArray(stepsProp.value) ? stepsProp.value : [];
        const activeIdx = stepperActive[line.uuid] || 0;

        if (!line.items) {
          line.items = [];
        }
        if (!line.items[activeIdx]) {
          line.items[activeIdx] = { items: [] };
        }
        if (!Array.isArray(line.items[activeIdx].items)) {
          line.items[activeIdx].items = [];
        }

        const stepItems = line.items[activeIdx].items;

        const handleStepClick = (idx) => {
          setStepperActive((prev) => ({ ...prev, [line.uuid]: idx }));
        };

        const dropToStepper = (ln, p) => {
          const removedIndex = p ? p.removedIndex : null;
          const addedIndex = p ? p.addedIndex : null;
          if (removedIndex === null && addedIndex === null) {
            return;
          }

          if (p) {
            if (
              p.payload &&
              p.payload.element &&
              p.payload.element.isMetadata &&
              !p.payload.element.uuid
            ) {
              p.payload.element.uuid = uuid();
            } else if (p.payload && p.payload.isMetadata && !p.payload.uuid) {
              p.payload.uuid = uuid();
            }
          }

          if (!ln.items) {
            ln.items = [];
          }
          const currentActiveIdx = stepperActive[ln.uuid] || 0;
          if (!ln.items[currentActiveIdx]) {
            ln.items[currentActiveIdx] = { items: [] };
          }
          if (!Array.isArray(ln.items[currentActiveIdx].items)) {
            ln.items[currentActiveIdx].items = [];
          }
          const list = ln.items[currentActiveIdx].items;

          if (removedIndex !== null) {
            list.splice(removedIndex, 1);
          }

          if (addedIndex !== null) {
            if (p && p.payload && p.payload.openModal) {
              setPendingStepperDrop({
                stepperUuid: ln.uuid,
                stepIndex: currentActiveIdx,
                insertIndex: addedIndex,
              });
              openComponentModal(p);
              return;
            }

            const element = p.payload
              ? p.payload.element
                ? p.payload.element
                : p.payload
              : null;
            if (element) {
              const slot = { items: [element] };
              if (addedIndex >= 0 && addedIndex <= list.length) {
                list.splice(addedIndex, 0, slot);
              } else {
                list.push(slot);
              }
            }
          }

          setLines([...lines]);
        };

        return (
          <div key={line.uuid}>
            <StyledField
              item={line}
              handleDelete={handleDelete}
              handleEdit={handleEdit}
            >
              <div
                style={{
                  padding: "10px",
                  borderRadius: "4px",
                  marginTop: "30px",
                }}
              >
                <Stepper activeStep={activeIdx} orientation="horizontal">
                  {steps.map((st, i) => {
                    const stepLabel = st.name || `Etapa ${i + 1}`;
                    return (
                      <Step
                        key={st.id || st.name || i}
                        onClick={() => handleStepClick(i)}
                        sx={{ cursor: "pointer", maxWidth: 100 }}
                      >
                        <EllipsisStepButton
                          label={stepLabel}
                          onClick={() => handleStepClick(i)}
                        />
                      </Step>
                    );
                  })}
                </Stepper>

                <Container
                  shouldAcceptDrop={(opt, p) => shouldAcceptDrop(opt, p)}
                  orientation="vertical"
                  groupName="1"
                  behaviour="drop-zone"
                  shouldAnimateDrop={() => false}
                  getChildPayload={(index) => {
                    const slot = stepItems[index];
                    return slot?.items?.[0] || null;
                  }}
                  onDrop={(p) => dropToStepper(line, p)}
                  getGhostParent={(p) => document.body}
                  dragClass="dnd-dragclass"
                  style={{ minHeight: "120px", width: "100%", marginTop: 12 }}
                >
                  {stepItems.length === 0 ? (
                    <div
                      style={{
                        minHeight: "120px",
                        border: "1px dashed #bfbfbf",
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#888",
                        marginTop: "40px",
                      }}
                    >
                      {language.stepperDragAndDrop}
                    </div>
                  ) : (
                    stepItems.map((slot) => {
                      const child = slot?.items?.[0];
                      return child
                        ? renderLine(child.element ? child.element : child)
                        : null;
                    })
                  )}
                </Container>
              </div>
            </StyledField>
          </div>
        );
      }

      if (line.type === GRID || line.type === DYNAMICLIST) {
        let style;
        try {
          style = transform(
            `.value {` +
              line.properties.find((e) => e.id === "propertieCSS").value +
              "}",
          ).value;
        } catch {
          style = {};
        }
        return (
          <Draggable key={line.uuid}>
            <StyledField
              item={line}
              handleDelete={handleDelete}
              handleEdit={handleEdit}
            >
              <div
                style={{
                  marginBottom: "5px",
                  padding: "10px",
                  borderRadius: "4px",
                }}
              >
                <Grid
                  container
                  item
                  xs={12}
                  direction="row"
                  key={line.uuid}
                  style={{
                    padding: "1%",
                    backgroundColor: "#eeeeee",
                    flexWrap: "wrap",
                    ...style,
                  }}
                >
                  {line &&
                    line.items.map((linha, linhaIndex) => {
                      return (
                        <Grid
                          container
                          style={{ flexWrap: "nowrap", width: "100%" }}
                          key={`${line.uuid}-tr-${linha.id}+${linhaIndex}`}
                        >
                          {linha &&
                            linha.items.map((column, colunaIndex) => {
                              return (
                                <Grid
                                  item
                                  xs={Math.ceil(12 / linha.items.length)}
                                  key={`${line.uuid}-td-${column.id}+${colunaIndex}`}
                                  style={{
                                    minHeight: "300px",
                                    height: "100%",
                                    borderStyle: "solid",
                                    borderColor: "#fff",
                                    flexBasis: "25%",
                                  }}
                                >
                                  <Container
                                    shouldAcceptDrop={(opt, p) =>
                                      shouldAcceptDrop(opt, p)
                                    }
                                    orientation="both"
                                    groupName="1"
                                    behaviour="drop-zone"
                                    shouldAnimateDrop={() => false}
                                    getChildPayload={() => {
                                      setOriginColumn(colunaIndex);
                                      setOriginLine(linhaIndex);
                                      return column.items[0];
                                    }}
                                    onDrop={(p) =>
                                      dropToColumn(
                                        line,
                                        linha,
                                        column,
                                        colunaIndex,
                                        linhaIndex,
                                        p,
                                      )
                                    }
                                    getGhostParent={(p) => document.body}
                                    dragClass="dnd-dragclass"
                                    style={{ height: "100%", width: "100%" }}
                                  >
                                    {column.items && column.items[0]
                                      ? renderLine(
                                          column.items[0].element
                                            ? column.items[0].element
                                            : column.items[0],
                                        )
                                      : null}
                                  </Container>
                                </Grid>
                              );
                            })}
                        </Grid>
                      );
                    })}
                </Grid>
              </div>
            </StyledField>
          </Draggable>
        );
      }

      return (
        <Draggable key={line.uuid}>
          <StyledField
            item={line}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
          />
        </Draggable>
      );
    },
    [
      handleDelete,
      handleEdit,
      shouldAcceptDrop,
      stepperActive,
      openComponentModal,
      lines,
    ],
  );

  const handleDialogClose = useCallback(
    (both, params) => {
      if (params) {
        params.line.items[originLine].items[originColumn].items[0] = params.p
          .payload
          ? params.p.payload.element
            ? params.p.payload.element
            : params.p.payload
          : null;
      }

      setOpenModalDecision(false);
      if (both) {
        setOpen(false);
      }
      setLines([...lines]);
    },
    [lines, originColumn, originLine],
  );

  const dropToColumnOk = useCallback(
    (params) => {
      params.linha.items[params.colNum].items[0] = params.p.payload
        ? params.p.payload.element
          ? params.p.payload.element
          : params.p.payload
        : null;
      setLines([...lines]);
    },
    [lines],
  );

  const onDrop = useCallback(
    ({ addedIndex, removedIndex, payload }, fromModal) => {
      let updatedLines = [...lines];
      let movedItem;
      if (removedIndex === null && addedIndex === null && !fromModal) {
        return;
      }

      if (fromModal && pendingStepperDrop) {
        const { stepperUuid, stepIndex, insertIndex } = pendingStepperDrop;
        const stepperIdx = updatedLines.findIndex(
          (it) => it?.uuid === stepperUuid,
        );
        if (stepperIdx !== -1) {
          const stepperLine = updatedLines[stepperIdx];
          let element = payload && payload.element ? payload.element : payload;

          if (element && element.isMetadata && !element.uuid) {
            element.uuid = uuid();
          }
          if (
            element &&
            (element.type === GRID || element.type === DYNAMICLIST) &&
            (!element.items || element.items.length === 0)
          ) {
            element = createGrid(Object.assign({}, element));
          }

          if (!Array.isArray(stepperLine.items)) {
            stepperLine.items = [];
          }
          const stepsProp = stepperLine?.properties?.find?.(
            (p) => p.id === "steps",
          ) || { value: [] };
          const stepsCount = Array.isArray(stepsProp.value)
            ? stepsProp.value.length
            : 0;

          for (let index = 0; index < stepsCount; index++) {
            if (!stepperLine.items[index]) {
              stepperLine.items[index] = { items: [] };
            }
            if (!Array.isArray(stepperLine.items[index].items)) {
              stepperLine.items[index].items = [];
            }
          }

          const list = stepperLine.items[stepIndex].items;

          const slot = { items: [element] };
          if (
            typeof insertIndex === "number" &&
            insertIndex >= 0 &&
            insertIndex <= list.length
          ) {
            list.splice(insertIndex, 0, slot);
          } else {
            list.push(slot);
          }

          setPendingStepperDrop(null);
          setLines([...updatedLines]);
          return;
        }
      }

      const hasStepperInForm = updatedLines.some((it) => it?.type === STEPPER);
      const isAddingNewAtRoot = removedIndex === null && addedIndex !== null;
      const isStepperPayload = !!(
        (payload && payload.type === STEPPER) ||
        (payload && payload.element && payload.element.type === STEPPER)
      );

      if (hasStepperInForm && isAddingNewAtRoot && !isStepperPayload) {
        const stepperIndex = updatedLines.findIndex(
          (it) => it?.type === STEPPER,
        );
        const stepperLine = updatedLines[stepperIndex];

        const stepperUuid = stepperLine.uuid;
        const activeIdx = stepperActive[stepperUuid] ?? 0;

        let element = payload && payload.element ? payload.element : payload;

        if (element && element.isMetadata && !element.uuid) {
          element.uuid = uuid();
        }

        if (
          element &&
          (element.type === GRID || element.type === DYNAMICLIST) &&
          (!element.items || element.items.length === 0)
        ) {
          element = createGrid(Object.assign({}, element));
        }

        if (!Array.isArray(stepperLine.items)) {
          stepperLine.items = [];
        }
        if (!stepperLine.items[activeIdx]) {
          stepperLine.items[activeIdx] = { items: [] };
        }
        if (!Array.isArray(stepperLine.items[activeIdx].items)) {
          stepperLine.items[activeIdx].items = [];
        }

        stepperLine.items[activeIdx].items.push({ items: [element] });

        setLines([...updatedLines]);
        return;
      }

      if (fromModal && removedIndex === null && addedIndex === null) {
        setEditComponent(payload);
        updatedLines = editItemOnGrid(payload, lines);

        setLines(updatedLines);
        return;
      }

      if (payload && (payload.type === GRID || payload.type === DYNAMICLIST)) {
        if (!payload.items || payload.items.length === 0) {
          movedItem = createGrid(Object.assign({}, payload));
        }
      }

      if (removedIndex !== null) {
        movedItem = updatedLines.splice(removedIndex, 1)[0];
      }
      if (addedIndex !== null) {
        updatedLines.splice(addedIndex, 0, movedItem || { ...payload });
      }

      const stepperIdx = updatedLines.findIndex(
        (it) => it && it.type === STEPPER,
      );
      if (stepperIdx > 0) {
        const [stepperItem] = updatedLines.splice(stepperIdx, 1);
        updatedLines.unshift(stepperItem);
      }

      setLines(updatedLines);
    },
    [lines, stepperActive, pendingStepperDrop, setEditComponent],
  );

  const openModal = useCallback(
    (p) => {
      if (p.payload && p.payload.openModal) {
        setOpen(true);
        setManage(Object.assign({}, p));
        closeComponentModal();
      } else {
        onDrop(Object.assign({}, p));
      }
    },
    [closeComponentModal, onDrop],
  );

  const handleOnDropComponent = useCallback(
    (p) => {
      if (p.payload && p.payload.openModal) {
        const isStepper = (objValue) =>
          !!objValue &&
          (objValue.type === STEPPER ||
            objValue?.payload?.type === STEPPER ||
            objValue?.payload?.element?.type === STEPPER);
        const hasStepperInForm = lines.some((it) => it?.type === STEPPER);

        if (isStepper(p) && hasStepperInForm) {
          setErrorMessage(language.onlyOneStepper);
          closeComponentModal();
          setErrorModalOpen(true);
          return;
        }

        if (!isStepper(p) && hasStepperInForm) {
          const stepperIndex = lines.findIndex((it) => it?.type === STEPPER);
          const stepperLine = lines[stepperIndex];
          const activeIdx = stepperActive[stepperLine.uuid] || 0;
          setPendingStepperDrop({
            stepperUuid: stepperLine.uuid,
            stepIndex: activeIdx,
            insertIndex: null,
          });
        }

        openComponentModal(p);
      } else {
        onDrop(Object.assign({}, p));
      }
    },
    [lines, stepperActive, onDrop, closeComponentModal, openComponentModal],
  );

  return (
    <React.Fragment>
      {open && (
        <PropertiesModal
          open={open}
          edit={edit}
          manage={manage}
          handleClose={handleClose}
          setEditComponent={setEditComponent}
          handleSubmit={onDrop}
          lines={lines}
          allFormFields={allFormFields}
          businessObject={businessObject}
        />
      )}

      {openComponent && (
        <ComponentModal
          open={openComponent}
          componente={manage}
          handleToggle={closeComponentModal}
          handleCreateComponent={openModal}
          metadatas={metadatas}
          onDrop={onDrop}
        />
      )}

      <Grid container>
        <Grid item size={2}>
          <Menu
            item={fields}
            metadatas={metadatas}
            canCreateMetadata={canCreateMetadata}
          />
        </Grid>

        <Grid item size={10} sx={{ p: 2 }}>
          <Container
            dropPlaceholder={true}
            shouldAnimateDrop={() => false}
            style={{
              border: "1px solid #c1c1c1",
              padding: "0.25%",
              height: "100%",
              overflowY: "scroll",
            }}
            groupName="1"
            onDrop={(p) => handleOnDropComponent(p)}
            getChildPayload={(index) => lines[index]}
            dragClass="dnd-dragclass"
          >
            {lines && lines.length === 0 ? (
              <h3 style={{ textAlign: "center" }}>{language.dragHere}</h3>
            ) : null}
            {lines?.map(renderLine)}
          </Container>
        </Grid>
      </Grid>

      <ModelerDecisionModal
        openDialog={openModalDecision}
        cellData={cellData}
        handleOk={dropToColumnOk}
        handleDialogClose={handleDialogClose}
      />

      {errorModalOpen && (
        <MaterialModal
          open={errorModalOpen}
          handleClose={() => setErrorModalOpen(false)}
          title={language.ntfy_type_error || "Erro"}
          size="sm"
          overwriteActions={() => (
            <Button
              color="primary"
              variant="contained"
              onClick={() => setErrorModalOpen(false)}
            >
              {language.ok || "OK"}
            </Button>
          )}
        >
          <div style={{ padding: "8px 0" }}>{errorMessage}</div>
        </MaterialModal>
      )}
    </React.Fragment>
  );
};

export default FormBuilder;
